import React, {useCallback, useEffect, useMemo} from 'react';

import Segment from './context/SegmentDto';
import {SegmentState} from './context/states';
import SegmentsTimeline from './SegmentsTimeline';
import SegmentList from './context/SegmentsListDto';
import CreateSegmentButton from './CreateSegmentButton';
import {ISegmentDto} from './context/SegmentDtoInterface';
import {SegmentProvider, useSegmentContext} from './context/context';

import './SegmentCreator.scss';

export type ExternalSegment = {
  endInSeconds: number;
  startInSeconds: number;
};

export type SegmentCreatorProps = {
  timelineMaxSeconds: number;
  timelineMinSeconds?: number;
  segments: Array<ExternalSegment>;

  onBlur: (externalSegmentsList: Array<ExternalSegment>) => void;
  onSegmentListEmptied?: () => void;
};

const SegmentCreatorBase = ({
  timelineMaxSeconds,
  timelineMinSeconds = 0,
  segments: rawSegments,
  onBlur,
  onSegmentListEmptied,
}: SegmentCreatorProps) => {
  const [segments, setSegments] = React.useState<ExternalSegment[]>(rawSegments);
  const {
    addState,
    resetState,

    segmentList,
    addSegment,
    removeState,
    updateSegment,
    removeSegment,
    setActiveSegment,
  } = useSegmentContext();

  const handleCreateHover = useCallback(() => {
    addState(SegmentState.PRE_CREATING);
  }, [addState]);

  const handleCreateBlur = useCallback(() => {
    resetState();
  }, [resetState]);

  const handleCreateClick = useCallback(async () => {
    removeState(SegmentState.PRE_CREATING);
    addState(SegmentState.CREATING);

    await addSegment();
  }, [addState, addSegment, removeState]);

  const handleHoverSegment = useCallback(
    (id: string) => {
      addState(SegmentState.PRE_EDITING);
      setActiveSegment(id);
    },
    [addState, setActiveSegment]
  );

  const handleUpdateSegment = useCallback(
    async (id: string, newStart: number, newEnd: number) => {
      removeState(SegmentState.PRE_EDITING);
      addState(SegmentState.EDITING);

      return await updateSegment(id, newStart, newEnd);
    },
    [addState, updateSegment, removeState]
  );

  const handleDeleteSegment = useCallback(
    async (id: string) => {
      let missionAccomplished = false;

      removeState(SegmentState.PRE_REMOVING);
      addState(SegmentState.REMOVING);

      missionAccomplished = await removeSegment(id);

      if (missionAccomplished) {
        resetState();
      }

      return missionAccomplished;
    },
    [addState, removeState, resetState, removeSegment]
  );

  const handleSegmentBlur = useCallback(() => {
    resetState();
  }, [resetState]);

  const convertSecondsToSegmentPoint = useCallback(
    (seconds: number): ISegmentDto['end'] | ISegmentDto['start'] => {
      const percentage =
        (seconds - timelineMinSeconds) / (timelineMaxSeconds - timelineMinSeconds);

      return (
        SegmentList.MIN_VALUE +
        percentage * (SegmentList.MAX_VALUE - SegmentList.MIN_VALUE)
      );
    },
    [timelineMinSeconds, timelineMaxSeconds]
  );

  useEffect(
    function parseExternalSegmentsToInternalModel() {
      if (JSON.stringify(rawSegments) === JSON.stringify(segments)) {
        setSegments(rawSegments);
        resetState();

        segments.forEach(({startInSeconds, endInSeconds}) => {
          addSegment(
            new Segment(
              convertSecondsToSegmentPoint(startInSeconds),
              convertSecondsToSegmentPoint(endInSeconds)
            )
          );
        });
      }
    },
    [segments, rawSegments, addSegment, convertSecondsToSegmentPoint, resetState]
  );

  const convertSegmentPointToSeconds = useCallback(
    (segmentPoint: ISegmentDto['end'] | ISegmentDto['start']): number => {
      const percentage =
        (segmentPoint - SegmentList.MIN_VALUE) /
        (SegmentList.MAX_VALUE - SegmentList.MIN_VALUE);

      return timelineMinSeconds + percentage * (timelineMaxSeconds - timelineMinSeconds);
    },
    [timelineMaxSeconds, timelineMinSeconds]
  );

  const externalSegmentsFormmated = useMemo(() => {
    const validSegments = segmentList.getAllValidSegments().getAllSegments();

    return validSegments.map(segment => ({
      endInSeconds: convertSegmentPointToSeconds(segment.end),
      startInSeconds: convertSegmentPointToSeconds(segment.start),
    }));
  }, [convertSegmentPointToSeconds, segmentList]);

  useEffect(
    function notifyWhenSegmentListIsEmptied() {
      if (!externalSegmentsFormmated.length && onSegmentListEmptied) {
        onSegmentListEmptied();
      }
    },
    [externalSegmentsFormmated, onSegmentListEmptied]
  );

  const handleSegmentCreatorOnBlur = useCallback(() => {
    onBlur(externalSegmentsFormmated);
  }, [onBlur, externalSegmentsFormmated]);

  return (
    <section className="segment-creator" onMouseLeave={handleSegmentCreatorOnBlur}>
      <CreateSegmentButton
        onBlur={handleCreateBlur}
        onHover={handleCreateHover}
        onClick={handleCreateClick}
      />

      <SegmentsTimeline
        onSegmentBlur={handleSegmentBlur}
        onSegmentHover={handleHoverSegment}
        onSegmentRemove={handleDeleteSegment}
        onSegmentUpdate={handleUpdateSegment}
        convertSegmentPointToSeconds={convertSegmentPointToSeconds}
      />
    </section>
  );
};

function SegmentCreator(props: SegmentCreatorProps) {
  return (
    <SegmentProvider>
      <SegmentCreatorBase {...props} />
    </SegmentProvider>
  );
}

export default SegmentCreator;
